import * as React from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { AppIcon } from '@/modules/shared/components/ui/AppIcon';
import { IconGhostButton } from '@/modules/shared/components/ui/button';

/**
 * MultiSelect Component - Material Design Responsive
 * 
 * Per copilot-instructions.md section 7B:
 * - Touch targets MUST be ≥ 48px on mobile
 * - Professional dropdown with multi-selection support
 * - Fixed dimensions to prevent layout shifts
 */

export interface MultiSelectOption {
  value: string;
  label: string;
  /** Optional secondary line (e.g. role key/code, category) shown under the label and included in search. */
  description?: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  className?: string;
}

export function MultiSelect({
  options,
  value,
  onChange,
  placeholder = 'Select options...',
  disabled = false,
  error = false,
  className = '',
}: MultiSelectProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter options based on search term
  const filteredOptions = React.useMemo(() => {
    if (!searchTerm) return options;
    const lowerSearch = searchTerm.toLowerCase();
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(lowerSearch) ||
        (option.description?.toLowerCase().includes(lowerSearch) ?? false),
    );
  }, [options, searchTerm]);

  const toggleOption = (optionValue: string) => {
    const newValue = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];
    onChange(newValue);
  };

  const clearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };

  const selectedLabels = value
    .map((v) => options.find((o) => o.value === v)?.label || v)
    .filter(Boolean);

  const handleTriggerClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`} onKeyDown={handleKeyDown}>
      {/* Trigger Button - Fixed height to prevent layout shifts */}
      <button
        type="button"
        onClick={handleTriggerClick}
        disabled={disabled}
        className={`
          flex h-10 w-full items-center justify-between 
          rounded-md border bg-background px-3 py-2 text-left text-sm 
          ring-offset-background transition-colors
          focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
          disabled:cursor-not-allowed disabled:opacity-50
          ${error ? 'border-destructive focus:ring-destructive' : 'border-border'}
          ${isOpen ? 'ring-2 ring-primary ring-offset-2' : ''}
        `}
      >
        <div className="flex-1 truncate">
          {value.length === 0 ? (
            <span className="text-muted-foreground">{placeholder}</span>
          ) : value.length === 1 ? (
            <span className="text-foreground">{selectedLabels[0]}</span>
          ) : (
            <span className="text-foreground">{value.length} roles selected</span>
          )}
        </div>
        <div className="flex items-center gap-1 ml-2 flex-shrink-0">
          {value.length > 0 && (
            <IconGhostButton
              type="button"
              onClick={clearAll}
              icon={X}
              tooltip="Clear all selected roles"
              disabled={disabled}
              className="h-6 w-6 min-h-0 min-w-0 rounded"
            />
          )}
          <AppIcon
            icon={ChevronDown}
            size="button"
            variant="muted"
            className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {/* Dropdown Content - Fixed dimensions */}
      {isOpen && (
        <div
          className="absolute z-[1400] mt-1 w-full rounded-md border border-border bg-surface-card shadow-lg"
          style={{ width: '100%' }}
        >
          {/* Search Input */}
          <div className="p-2 border-b border-border-soft">
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              className="w-full px-2 py-1.5 text-sm border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Options List - Fixed height with scroll */}
          <div className="overflow-y-auto" style={{ maxHeight: '160px' }}>
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-3 text-sm text-muted-foreground text-center">
                {searchTerm ? 'No roles found' : 'No roles available'}
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = value.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => toggleOption(option.value)}
                    className={`
                      w-full flex items-center gap-2 px-3 py-2 text-left text-sm
                      hover:bg-surface-hover transition-colors cursor-pointer
                      ${isSelected ? 'bg-primary-soft' : ''}
                    `}
                  >
                    <div
                      className={`
                        flex-shrink-0 h-4 w-4 rounded border 
                        flex items-center justify-center transition-colors
                        ${isSelected ? 'bg-primary border-primary' : 'border-border bg-background'}
                      `}
                    >
                      {isSelected && <Check className="h-3 w-3 text-card" />}
                    </div>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-foreground">{option.label}</span>
                      {option.description && (
                        <span className="block truncate text-xs text-muted-foreground">{option.description}</span>
                      )}
                    </span>
                  </button>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="px-3 py-1.5 border-t border-border-soft bg-muted text-xs text-muted-foreground flex justify-between items-center">
            <span>{value.length} selected</span>
            {value.length > 0 && (
              <button
                type="button"
                onClick={clearAll}
                className="text-primary hover:text-primary-hover font-medium"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

